import { Service } from '@shared/service/models';
import { getServicesDurationForPartySize } from '@shared/service/functions';

export interface AutoDurationRecalculateConfig {
  getServices: () => Service[];
  getSelectedServiceIds: () => string[];
  getPartySize: () => number;
  getDuration: () => number;
  applyDuration: (duration: number) => void;
}

export class AutoDurationRecalculator {
  private lastAutoDuration: number | null = null;
  private lastSelectedServiceIds: string[] | null = null;

  constructor(private readonly config: AutoDurationRecalculateConfig) {
  }

  setBaseline(autoDuration: number, selectedServiceIds: string[]): void {
    this.lastAutoDuration = autoDuration;
    this.lastSelectedServiceIds = [...(selectedServiceIds ?? [])];
  }

  recompute(): void {
    const services = this.config.getServices();
    const selectedServiceIds = this.config.getSelectedServiceIds() ?? [];
    const partySize = this.config.getPartySize();

    if (selectedServiceIds.length === 0) {
      this.lastAutoDuration = null;
      this.lastSelectedServiceIds = null;

      return;
    }

    const selectionChanged = this.lastSelectedServiceIds !== null
      && !this.isSameServiceSelection(this.lastSelectedServiceIds, selectedServiceIds);

    let next: number | null;
    if (selectionChanged) {
      const duration = getServicesDurationForPartySize(services, selectedServiceIds, partySize);
      next = duration > 0 ? duration : null;
    } else {
      next = this.getAutoDurationForChange(
        services,
        selectedServiceIds,
        partySize,
        this.config.getDuration(),
        this.lastAutoDuration,
      );
    }

    if (next !== null) {
      this.lastAutoDuration = next;
      this.lastSelectedServiceIds = [...selectedServiceIds];
      this.config.applyDuration(next);
    }
  }

  /**
   * Decides the service duration to auto-apply when party size or selected services change.
   * Returns the new duration, or null to leave the current value untouched — the latter when the
   * user has manually edited the duration (detected because it differs from the last value we auto-set).
   */
  getAutoDurationForChange(
    services: Service[],
    selectedServiceIds: string[],
    partySize: number | undefined,
    currentDuration: number,
    lastAutoDuration: number | null,
  ): number | null {
    if (lastAutoDuration !== null && currentDuration !== lastAutoDuration) {
      return null;
    }

    const duration = getServicesDurationForPartySize(services, selectedServiceIds, partySize);

    return duration > 0 ? duration : null;
  }

  private isSameServiceSelection(a: string[], b: string[]): boolean {
    if (a.length !== b.length) {
      return false;
    }

    const idsInB = new Set(b);

    return a.every(id => idsInB.has(id));
  }
}
